import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { useFeatureVersionControls } from '@hooks/useFeatureGate';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { startPreview } from '@utils/featureRollout/preview';
import { getActiveCommerceId } from '@utils/featureRollout/cache';
import { isRoleRejected, subscribeRoleRejection } from '@utils/featureRollout/roleRejection';
import { requestRevertFeedback } from '@utils/featureRollout/revertFeedback';
import { KEEP_NEW, ROLE_CTA } from '@utils/featureRollout/strings';
import { V4 } from '../tokens';

// Chip DUAL del canal PULL, anclado a la línea del título de la página (NO un banner sobre el
// contenido ni una barra en una esquina muerta). Resultado del panel UX (5 expertos, 5/5):
// un CHIP corto y descubrible en el header. Self-contained: consume el hook directamente.
//
// Dos modos según la variante resuelta (acotados por `scope` para evitar duplicación: el shell
// del menú monta scope='pull' y la página V2 monta scope='status'):
//   - CONTROL    → CTA por rol → switchTo('treatment', 'chip_pull') / preview. Canal PULL.
//   - TREATMENT  → indicador "Versión nueva" + popover Comparar (compare_peek) / Volver
//                  (revert_commit).
//
// Visibilidad: manual_entry!=='hidden' (GA | evacuado | opt-out del chip ocultan el PULL). El
// CTA de control además SOLO aparece con inviteSurface==='none' (DEDUP: con la card PUSH activa
// ella ya muestra el CTA — no dos botones idénticos). El indicador de treatment requiere que el
// contrato compartido entregue las acciones de gestión de versión (`useFeatureVersionControls`:
// acciones o null — nunca un booleano que se pueda olvidar de consultar). El prompt de feedback
// al revertir lo renderiza MenuVersionInvitation (signal de módulo), no este chip, para que
// sobreviva al desmontaje de la página treatment.

const CheckIcon: ReactNode = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M11 5l-7 7 7 7M4 12h16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CompareIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Icono del CTA "Quedarme con la versión nueva": un corazón (afirmación positiva de permanencia).
const HeartIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path
      d="M12 21s-7.5-4.6-10-9.3C.4 8.4 2 5 5.3 5c2 0 3.4 1.1 4.7 2.7C11.3 6.1 12.7 5 14.7 5 18 5 19.6 8.4 22 11.7 19.5 16.4 12 21 12 21z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icono del CTA PULL en control ("probar la versión nueva"): un destello/sparkle.
const SparkIcon: ReactNode = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path
      d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"
      strokeLinecap="round"
    />
  </svg>
);

const Caret: FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
    style={{ transition: 'transform .15s ease', transform: open ? 'rotate(180deg)' : 'none' }}
  >
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const chipButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.successFaded,
  color: V4.successDark,
  border: `1px solid ${V4.successDark}22`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

// Chip del canal PULL en control: CTA "probar la versión nueva". Tono de marca (secondary)
// para diferenciarlo del chip verde de "ya estás en la nueva".
const pullChipButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.secondaryFaded,
  color: V4.secondaryDark,
  border: `1px solid ${V4.secondaryDark}22`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

const popoverStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 'min(260px, 86vw)',
  background: V4.white,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 10,
  boxShadow: '0 12px 28px rgba(37,55,70,.15)',
  padding: 14,
  zIndex: 40,
  textAlign: 'left',
};

// Acción PRIMARIA del popover: "Quedarme con la versión nueva" (adopción explícita, trigger
// 'kept'). Botón con relleno: es la vía afirmativa que queremos destacar. Cuenta como adopción y
// dispara la encuesta de permanencia.
const keepButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  width: '100%',
  background: V4.successDark,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '9px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
};

// Acción SECUNDARIA: comparar (peek). Outline para ceder protagonismo al CTA "Quedarme". Mirar
// la anterior sin rechazar (la barra sticky aparece y se puede volver a la nueva).
const compareButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  width: '100%',
  background: V4.white,
  color: V4.secondaryDark,
  border: `1.5px solid ${V4.secondary}`,
  borderRadius: 8,
  padding: '9px 12px',
  marginTop: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
};

// Acción SECUNDARIA: volver definitivo (rechazo). Link discreto con confirmación inline para
// que no se confunda con el peek reversible.
const revertLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  background: 'transparent',
  color: V4.grayUltraDark,
  border: 0,
  padding: '8px 6px',
  marginTop: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
};

// Botones de la confirmación inline del "volver definitivo".
const confirmRevertButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  flex: 1,
  background: V4.white,
  color: V4.secondaryDark,
  border: `1.5px solid ${V4.secondary}`,
  borderRadius: 8,
  padding: '8px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 700,
};

const cancelRevertButton: CSSProperties = {
  flex: 1,
  background: 'transparent',
  color: V4.grayUltraDark,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: '8px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
};

interface MenuVersionStatusChipProps {
  // Ámbito del chip, para evitar la duplicación (el shell del menú y la página V2 montan cada uno
  // su instancia): 'pull' = solo el CTA de control (canal PULL); 'status' = solo el indicador de
  // treatment. Sin scope = ambos (compatibilidad).
  scope?: 'pull' | 'status';
}

const MenuVersionStatusChip: FC<MenuVersionStatusChipProps> = ({ scope }) => {
  const { variant, gaForced, compareActive, manualEntry, inviteSurface, isEligibleInvitation, switchTo } =
    useFeatureVersion(MENU_FEATURE_KEY);
  // Gestión de versión (volver / quedarme) por el contrato compartido: acciones o `null`. El
  // `gaForced` de arriba sigue aquí porque además apaga el chip PULL (invitación), que NO es
  // gestión de versión — son dos gates distintos que casualmente coinciden en GA.
  const versionControls = useFeatureVersionControls(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();
  // Rechazo de rol del backend (REQUIRES_ROOT) en esta sesión: si está, el usuario NO es root
  // efectivo aunque el FE lo crea → ofrecemos preview en vez del CTA durable (rompe el loop).
  const subscribeRole = useCallback((cb: () => void) => subscribeRoleRejection(MENU_FEATURE_KEY, cb), []);
  const getRoleRejected = useCallback(() => isRoleRejected(getActiveCommerceId(), MENU_FEATURE_KEY), []);
  const roleRejected = useSyncExternalStore(subscribeRole, getRoleRejected, getRoleRejected);
  const effectiveRoot = isRoot === true && !roleRejected;
  const [open, setOpen] = useState(false);
  const [confirmingRevert, setConfirmingRevert] = useState(false);
  const [softNotice, setSoftNotice] = useState<string | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);

  // Cerrar al click-fuera y con Escape (devolviendo el foco al chip).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirmingRevert(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setConfirmingRevert(false);
        chipRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Devuelve true si el usuario puede cambiar la versión (root efectivo). Si no, deja el aviso
  // suave. Usa effectiveRoot: si el backend ya rechazó por rol esta sesión, NO es root aunque el
  // FE lo creyera (evita reintentar un cambio que volverá a fallar).
  const guardRoot = (): boolean => {
    if (!effectiveRoot) {
      setSoftNotice('Solo el administrador principal (root) puede cambiar la versión de este comercio.');
      return false;
    }
    setSoftNotice(null);
    return true;
  };

  // Visibilidad del canal PULL: el chip vive mientras manual_entry='available' (GA | evacuado |
  // opt-out del chip lo ocultan) y durante un peek la barra sticky lo cubre.
  if (manualEntry === 'hidden' || gaForced || compareActive) return null;

  // Modo CONTROL: chip CTA por ROL (canal PULL). Solo si el comercio es elegible (hay experimento
  // activo, no GA, no evacuado) Y el rol ya resolvió (Item E#4: el CTA decide por rol EN RENDER;
  // sin spinner de pantalla entera — simplemente no mostramos el chip PULL hasta resolver isRoot).
  // DEDUP: el chip PULL solo aparece cuando NO hay card PUSH activa (inviteSurface='none'); con la
  // card visible (modal/card) ella ya muestra el CTA — evitamos dos botones idénticos. Al
  // descartar/snoozear la invitación (inviteSurface→'none') el chip reaparece como entrada discreta.
  // scope='status' suprime este modo (esa instancia solo muestra el indicador de treatment).
  const roleResolved = isRoot !== undefined;
  const showPullChip =
    scope !== 'status' && variant === 'control' && isEligibleInvitation && roleResolved && inviteSurface === 'none';
  // Modo TREATMENT: indicador "versión nueva" + popover Comparar/Volver. Se muestra solo si el
  // contrato entrega las acciones de gestión (can_revert y no graduada).
  // scope='pull' suprime este modo (esa instancia solo muestra el CTA de control).
  const showStatusChip = scope !== 'pull' && variant === 'treatment' && versionControls !== null;
  if (!showPullChip && !showStatusChip) return null;

  // MODO CONTROL: CTA por ROL (Item E#1, decisión EN RENDER con isRoot resuelto).
  //   - root efectivo → adopta la versión nueva (switch DURABLE per-comercio, trigger chip_pull).
  //   - no-root       → echa un vistazo (vista previa PERSONAL per-usuario, sin tocar el comercio).
  // Ya NO se bloquea al no-root con un aviso: tiene una acción real (mirar la nueva). Si el backend
  // rechazó por rol esta sesión (effectiveRoot=false), también cae al preview → no reintenta el PUT.
  const onTryNew = () => {
    if (effectiveRoot) {
      switchTo('treatment', 'chip_pull');
      return;
    }
    startPreview(MENU_FEATURE_KEY);
  };

  // ACCIÓN PRIMARIA (afirmativa): "Quedarme con la versión nueva" → adopción explícita (trigger
  // 'kept'). Solo root efectivo (es un cambio durable del comercio: deja state='adopted'). Cierra
  // el popover y dispara la encuesta de permanencia (la renderiza el shell como overlay).
  const onKeep = () => {
    if (!guardRoot()) return;
    setOpen(false);
    versionControls?.keepCurrent('kept');
  };

  // ACCIÓN SECUNDARIA: comparar (peek). Mirar la anterior sin rechazar — la barra sticky aparece
  // y se puede volver a la nueva. trigger compare_peek (el backoffice no toca la membresía).
  const onCompare = () => {
    if (!guardRoot()) return;
    setOpen(false);
    switchTo('control', 'compare_peek');
  };

  // ACCIÓN SECUNDARIA: volver definitivo (rechazo). Confirmación inline antes de comprometer.
  const onRevertConfirmed = () => {
    if (!guardRoot()) return;
    setConfirmingRevert(false);
    setOpen(false);
    versionControls?.revert('revert_commit');
    // Pide el prompt de feedback (D1) vía signal de módulo. El revert YA se ejecutó arriba; al
    // pasar a control este chip de status se desmonta (MenuV2 desaparece), por eso el prompt lo
    // RENDERIZA MenuVersionInvitation (persiste en el shell). Omitible y fire-and-forget.
    requestRevertFeedback(MENU_FEATURE_KEY);
  };

  // MODO CONTROL: chip PULL simple (sin popover). CTA por rol: root deja activa la nueva
  // (durable); no-root echa un vistazo (preview personal). Si justo antes se revirtió, el prompt
  // de feedback (D1) acompaña al chip.
  if (showPullChip) {
    // Contenedor block con padding: el chip de control vive en el shell (sobre la página V1, que
    // no tiene slot de título propio). Sin padding quedaba PEGADO al header y sin alinear con el
    // contenido. El padding lo separa del header (top) y lo indenta hacia la columna de contenido.
    // Como este render es condicional, en treatment (chip null) no agrega espacio.
    return (
      <span ref={wrapRef} style={{ display: 'flex', padding: '12px 0 4px 24px' }}>
        <button ref={chipRef} type="button" style={pullChipButton} onClick={onTryNew}>
          {SparkIcon}
          {effectiveRoot ? ROLE_CTA.rootTry : ROLE_CTA.operatorPreview}
        </button>
      </span>
    );
  }

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={chipRef}
        type="button"
        style={chipButton}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setConfirmingRevert(false);
        }}
      >
        {CheckIcon}
        Versión nueva
        <Caret open={open} />
      </button>

      {open && (
        <span role="dialog" aria-label="Versión del menú" style={popoverStyle}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: V4.primary }}>
            Estás usando el menú nuevo
          </span>
          <span
            style={{ display: 'block', fontSize: 12, color: V4.grayUltraDark, margin: '4px 0 12px', lineHeight: 1.45 }}
          >
            Rediseñamos esta sección. Puedes compararla con la anterior sin perder tu lugar — y volver a la nueva cuando
            quieras.
          </span>

          {/* PRIMARIA (afirmativa): quedarse con la versión nueva (adopción explícita, trigger
              'kept'). Dispara la encuesta de permanencia. */}
          <button type="button" style={keepButton} onClick={onKeep}>
            {HeartIcon}
            {KEEP_NEW.cta}
          </button>

          {/* SECUNDARIA: comparar (peek reversible). */}
          <button type="button" style={compareButton} onClick={onCompare}>
            {CompareIcon}
            Comparar con la anterior
          </button>

          {/* SECUNDARIA: volver definitivo, con confirmación inline. */}
          {!confirmingRevert ? (
            <button
              type="button"
              style={revertLink}
              onClick={() => {
                if (guardRoot()) setConfirmingRevert(true);
              }}
            >
              {BackIcon}
              Volver a la versión anterior
            </button>
          ) : (
            <span style={{ display: 'block', marginTop: 8 }}>
              <span
                style={{ display: 'block', fontSize: 11.5, color: V4.grayUltraDark, marginBottom: 8, lineHeight: 1.4 }}
              >
                ¿Volver a la versión anterior? Podrás regresar a la nueva cuando quieras.
              </span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={confirmRevertButton} onClick={onRevertConfirmed}>
                  {BackIcon}
                  Sí, volver
                </button>
                <button type="button" style={cancelRevertButton} onClick={() => setConfirmingRevert(false)}>
                  Cancelar
                </button>
              </span>
            </span>
          )}

          {softNotice && (
            <span
              style={{
                display: 'block',
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 8,
                background: V4.warningFaded,
                color: V4.warningDark,
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {softNotice}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default MenuVersionStatusChip;
